import React, { useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { gql, useQuery } from "@apollo/client";
import { pickupRestaurantsNear } from "../../apollo/server";
import { BUTTON_RESET, C, RADIUS, TYPE } from "../../utils/designTokens";
import { LINK_SX } from "../RestaurantList/listUi";
import PickupCard from "./PickupCard";
import PickupMapDialog from "./PickupMapDialog";
import PreferDelivery from "./PreferDelivery";
import DeliveryWaitlist from "./DeliveryWaitlist";
import useDriveTimes from "./useDriveTimes";
import { radiusText, streetOf, townOf } from "./pickupText";

const PICKUP_NEAR = gql`${pickupRestaurantsNear}`;

// A green map pin with a dotted route that stops short of it.
function RouteShortOfPin() {
  return (
    <svg width="34" height="34" viewBox="0 0 34 34" aria-hidden="true" focusable="false">
      <path d="M4 29 C 9 29, 9 21, 14 21 S 19 16, 20 14" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeDasharray="0.1 4" fill="none" />
      <path d="M26 3.5c-3.6 0-6.5 2.8-6.5 6.4 0 4.6 6.5 11.6 6.5 11.6s6.5-7 6.5-11.6c0-3.6-2.9-6.4-6.5-6.4z" fill={C.green} />
      <circle cx="26" cy="9.8" r="2.4" fill="#FFFFFF" />
    </svg>
  );
}

/** The page's heading when nothing delivers here. */
function NoDeliveryNotice({ title = "Delivery isn't available here yet", body = "But you can pick up from these restaurants near you." }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, maxWidth: 720, p: { xs: 2, sm: 2.5 }, borderRadius: RADIUS.card, border: `1.5px solid ${C.greenSoft}`, bgcolor: C.greenTint }}>
      <Box sx={{ width: 56, height: 56, flexShrink: 0, borderRadius: "50%", bgcolor: C.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <RouteShortOfPin />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Box component="h1" sx={{ ...TYPE.cardTitle, fontSize: { xs: 19, sm: 22 }, m: 0, color: C.ink }}>
          {title}
        </Box>
        <Box component="p" sx={{ ...TYPE.body, fontSize: 15, m: 0, mt: 0.5, color: C.ink }}>
          {body}
        </Box>
      </Box>
    </Box>
  );
}

/**
 * The restaurant list when no restaurant delivers to the chosen address (the
 * customer app's Home/Pickup/PickupHome). Lists pickup restaurants within the
 * admin's pickup radius, the customer's saved addresses that do get
 * delivery, and the waitlist / suggestion card. `isPickup`: the customer is
 * already in pickup mode, so the top says "Pickup near you" instead.
 * `deliveryHere`: something does deliver here (pickup mode, nothing in the
 * delivery-zone list offers pickup), so the delivery cards are left out.
 */
function PickupHome({ isPickup, deliveryHere = false, location, onOrder, onUseAddress, onMessage }) {
  const [mapOpen, setMapOpen] = useState(false);
  const point = { latitude: Number(location?.latitude), longitude: Number(location?.longitude) };
  const { data, loading, error, refetch } = useQuery(PICKUP_NEAR, {
    variables: point,
    skip: !Number.isFinite(point.latitude) || !Number.isFinite(point.longitude),
    fetchPolicy: "cache-and-network",
  });
  const restaurants = data?.pickupRestaurantsNear?.restaurants || [];
  const radiusMiles = data?.pickupRestaurantsNear?.radiusMiles;
  const driveMinutes = useDriveTimes(location, restaurants);
  const city = townOf(location?.deliveryAddress || location?.label);
  // A failed request is not "nothing nearby": that would tell someone with a
  // pickup place down the road there is none.
  const none = !loading && !error && Boolean(data) && restaurants.length === 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "32px" }}>
      {none ? (
        <NoDeliveryNotice
          title={deliveryHere ? "No pickup near you yet" : "No restaurants near you yet"}
          body={
            deliveryHere
              ? `No restaurant within ${radiusText(radiusMiles)} of ${streetOf(location)} offers pickup. Switch to Delivery to see who delivers here.`
              : `Nothing delivers to ${streetOf(location)}, and no restaurant within ${radiusText(radiusMiles)} offers pickup.`
          }
        />
      ) : isPickup ? (
        <Box component="header">
          <Box component="h1" sx={{ ...TYPE.pageTitle, fontSize: { xs: 28, sm: 34 }, m: 0, color: C.ink }}>
            Pickup near you
          </Box>
          <Box component="p" sx={{ ...TYPE.body, fontSize: 15, m: 0, mt: 0.75, color: C.muted }}>
            Order ahead, skip the line, collect when it's ready.
          </Box>
        </Box>
      ) : (
        <NoDeliveryNotice />
      )}

      {!none && (
        <Box component="section" aria-labelledby="pickup-near-title">
          <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 2, mb: 2 }}>
            <Box sx={{ minWidth: 0 }}>
              <Box component="h2" id="pickup-near-title" sx={{ ...TYPE.sectionTitle, m: 0, color: C.ink }}>
                Available for pickup
              </Box>
              <Box component="p" sx={{ ...TYPE.body, m: 0, mt: 0.5, color: C.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                Within {radiusText(radiusMiles)} of {streetOf(location)}
              </Box>
            </Box>
            {restaurants.length > 0 && (
              <Box component="button" type="button" onClick={() => setMapOpen(true)} sx={{ ...BUTTON_RESET, ...LINK_SX, flexShrink: 0 }}>
                See map
              </Box>
            )}
          </Box>

          {loading && restaurants.length === 0 ? (
            <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={36} sx={{ color: C.red }} aria-label="Loading pickup restaurants" />
            </Box>
          ) : error && restaurants.length === 0 ? (
            <Box sx={{ py: 5, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
              <Box component="p" sx={{ ...TYPE.body, m: 0, color: C.muted }}>
                Couldn't load pickup restaurants.
              </Box>
              <Box component="button" type="button" onClick={() => refetch()} sx={{ ...BUTTON_RESET, ...LINK_SX }}>
                Try again
              </Box>
            </Box>
          ) : (
            <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none", display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 280px), 1fr))", gap: "28px 20px" }}>
              {restaurants.map((r) => (
                <Box component="li" key={r._id} sx={{ display: "flex", minWidth: 0, "& > article": { flex: 1 } }}>
                  <PickupCard r={r} driveMinutes={driveMinutes(r)} onOrder={onOrder} />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      {!deliveryHere && (
        <>
          <PreferDelivery currentAddressId={location?._id} currentAddress={location?.deliveryAddress} onUse={onUseAddress} />
          <DeliveryWaitlist city={city} location={location} onMessage={onMessage} />
        </>
      )}

      <PickupMapDialog
        open={mapOpen}
        onClose={() => setMapOpen(false)}
        origin={location}
        restaurants={restaurants}
        driveMinutes={driveMinutes}
        onOrder={(r) => {
          setMapOpen(false);
          onOrder(r);
        }}
      />
    </Box>
  );
}

export default PickupHome;
